import type { ReactNode } from "react";

export type QrFeatureRow = {
  iconClass: string;
  bgClass: string;
  iconColor: string;
  title: string;
  description: string;
};

type QrLinkGuestPromptProps = {
  welcomeTitle: string;
  welcomeSubtitle: string;
  loginRequiredTitle: string;
  loginRequiredDesc: string;
  features: QrFeatureRow[];
  signupSlot: ReactNode;
  loginSlot: ReactNode;
  errorDetail?: string;
};

export default function QrLinkGuestPrompt({
  welcomeTitle,
  welcomeSubtitle,
  loginRequiredTitle,
  loginRequiredDesc,
  features,
  signupSlot,
  loginSlot,
  errorDetail,
}: QrLinkGuestPromptProps) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 to-purple-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-sm w-full">
        <div className="bg-gradient-to-r from-violet-500 to-purple-500 px-6 py-8 text-white text-center">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <i className="ri-qr-scan-2-line text-4xl"></i>
          </div>
          <h1 className="text-2xl font-bold mb-2">{welcomeTitle}</h1>
          <p className="text-violet-100 text-sm">{welcomeSubtitle}</p>
        </div>

        <div className="p-6">
          {errorDetail && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 text-xs text-red-700 break-all">
              {errorDetail}
            </div>
          )}

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
            <div className="flex gap-3">
              <i className="ri-information-line text-xl text-amber-600 flex-shrink-0 mt-0.5"></i>
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">{loginRequiredTitle}</p>
                <p className="text-amber-700">{loginRequiredDesc}</p>
              </div>
            </div>
          </div>

          <div className="space-y-3 mb-6">
            {features.map((f) => (
              <div
                key={f.title}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl"
              >
                <div
                  className={`w-10 h-10 ${f.bgClass} rounded-lg flex items-center justify-center flex-shrink-0`}
                >
                  <i className={`${f.iconClass} text-lg ${f.iconColor}`}></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{f.title}</p>
                  <p className="text-xs text-gray-500">{f.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-3">
            {signupSlot}
            {loginSlot}
          </div>
        </div>
      </div>
    </div>
  );
}
